import { keccak256, ethers, ZeroAddress, ZeroHash, solidityPacked } from 'ethers';
import { always, mergeAll, T, values, sort, intersection, unnest, flatten, toPairs } from 'ramda';
import { decode } from 'js-base64';
import { whenDefinedAll, whenDefined } from '@devprotocol/util-ts';
import { BigNumber } from 'bignumber.js';

const addresses = {
    eth: {
        main: {
            registry: '0x1D415aa39D647834786EB9B5a333A50e9935b796',
            sTokens: '0x50489Ff5f879A44C87bBA85287729D663b18CeD5',
        },
        ropsten: {
            registry: '0xD6D07f1c048bDF2B3d5d9B6c25eD1FC5348D0A70',
            sTokens: '0x7224e918923984b430eA4B96C4C523Da6e8d7F9D',
        },
    },
    arbitrum: {
        one: {
            token: '0x91F5dC90979b058eBA3be6B7B7e523df7e84e137',
            lockup: '0x1A2B49e10013C40AAC9b6f9e785837bfd329e5e0',
            marketFactory: '0xaF490bA3EaFf9495f01407EC22027bD90eaFABb1',
            metricsFactory: '0xEdCF7765B7Be6415D02173e4645604bB1A377929',
            policyFactory: '0x2ef61A14284A0f45813e492b3696F4BD2cc51B6f',
            propertyFactory: '0xa4C8A1E3012E8fBE21f2936DEab6dD50A0A05774',
            registry: '0xbeF4DeEA3AE863739Bd402E025c749536f491ffa',
            sTokens: '0x40d999931f7055F670511860e24624939e71a96a',
            withdraw: '0x1c2699e43F0Bc47b45F27BFc84e99c64851A4BF0',
        },
        rinkeby: {
            token: '0xc28BBE3B5ec1b06FDe258864f12c1577DaDFadDC',
            lockup: '0x4944CA0423f42DF7c77ad8Cd53F30f31A097F4fa',
            marketFactory: '0x6EA7a231dc747fBEf931639B15908625f31287bf',
            metricsFactory: '0x650663aD898A018cca44Ac224Be2286D14B7421d',
            policyFactory: '0x485085f157dD7Ba451f0AB6Fa2ca42421CBA1d3c',
            propertyFactory: '0x66446E01c5a2fdA8FC5Af640a12E66c078903487',
            registry: '0x519d5e729fbE6B3e4607260413Fb684759612465',
            sTokens: '0xe45d65c6d6aA3e2a4c8aAcc0C8153778663fe794',
            withdraw: '0xFBfB9aD095f906d7E354F7ee31d88F0ca517A9C0',
        },
    },
    polygon: {
        mainnet: {
            token: '0xA5577D1cec2583058A6Bd6d5DEAC44797c205701',
            lockup: '0x42767B12d3f07bE0D951a64eE6573B40Ff165C4e',
            marketFactory: '0xEA518Ab9551d3cD9Ec4C74df1fa6946B18212178',
            metricsFactory: '0xD18f2eB75cE3a6A35586115b2f2c63d907536CDE',
            policyFactory: '0xD42c04179410a38BE95737e663480a7CC42B9C05',
            propertyFactory: '0x8bB6a34afBD34663cEeEFA697918c647Da22a352',
            registry: '0xbeF4DeEA3AE863739Bd402E025c749536f491ffa',
            sTokens: '0x89904De861CDEd2567695271A511B3556659FfA2',
            withdraw: '0x60d1743fc6791aEDB833E4aED604eccb9EC838DE',
        },
        mumbai: {
            token: '0xcbc698ed514dF6e54932a22515d6D0C27E4DA091',
            lockup: '0xfDC5FF1F07871A247eafE14eEB134eeFcbCf1ceA',
            marketFactory: '0x953992D325d8A9Bab35406C2E34c0d5535fa5206',
            metricsFactory: '0x02d107567020bCE1Eb524D80C44f899Fa9993E5f',
            policyFactory: '0x0f85eC41980E86847Ba57eA763E9c5f88f829554',
            propertyFactory: '0xcCB7e1454d66fc3c2053694D323c127D2a4b39BA',
            registry: '0xe2C16936413D74c667aeF0D040c920BF639067d4',
            sTokens: '0xe0af15141ABd0B31Fb15e250971936Fe8837230a',
            withdraw: '0x5f9d9f40f4d7d8A350745D69e94E20339Df17b83',
        },
    },
};

const erc20Abi = [
    {
        inputs: [
            {
                internalType: 'string',
                name: 'name_',
                type: 'string',
            },
            {
                internalType: 'string',
                name: 'symbol_',
                type: 'string',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'symbol',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'decimals',
        outputs: [
            {
                internalType: 'uint8',
                name: '',
                type: 'uint8',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'recipient',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'transfer',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
        ],
        name: 'allowance',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'approve',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'sender',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'recipient',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'transferFrom',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'addedValue',
                type: 'uint256',
            },
        ],
        name: 'increaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'subtractedValue',
                type: 'uint256',
            },
        ],
        name: 'decreaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const pad = (args, index) => ((fn) => fn([], args[0], 0, fn))((arr, v, i, fn) => i < index ? fn(arr.concat(v ?? ''), args[i + 1], i + 1, fn) : arr);
const isBigNumber = (data) => typeof data === 'bigint';
const toString = (data) => data.toString();
const toStringObj = (data) => {
    const keys = Object.keys(data);
    const valueSet = keys.map((key) => ({
        [key]: ((value) => (isBigNumber(value) ? toString(value) : value))(data[key]),
    }));
    return mergeAll(valueSet);
};
const stringifyItem = (data) => {
    return isBigNumber(data)
        ? toString(data)
        : typeof data === 'string' ||
            typeof data === 'number' ||
            typeof data === 'boolean'
            ? data
            : toStringObj(data);
};
const stringify = (data) => {
    return data instanceof Array ? data.map(stringifyItem) : stringifyItem(data);
};
const N = null;
const execute = async (opts) => {
    const signer = typeof opts.contract?.runner?.getSigner === 'function'
        ? await opts.contract.runner
            .getSigner()
            .catch(always(undefined))
        : undefined;
    const contract = opts.mutation && signer
        ? opts.contract.connect(signer)
        : opts.contract;
    const convertedArgs = opts.args === undefined
        ? undefined
        : opts.args.map((v) => (v instanceof Uint8Array ? keccak256(v) : v));
    const args = convertedArgs === undefined
        ? undefined
        : opts.padEnd
            ? [...pad(convertedArgs, opts.padEnd)]
            : [...convertedArgs];
    const argsOverrided = opts.mutation && opts.overrides?.overrides
        ? [...(args || []), opts.overrides.overrides]
        : args;
    const typedMethod = `${opts.method}(${opts.interface})`;
    const method = opts.static
        ? opts.interface
            ? contract[typedMethod].staticCall
            : contract[opts.method].staticCall
        : opts.interface
            ? contract[typedMethod]
            : contract[opts.method];
    const res = await (argsOverrided === undefined
        ? method()
        : method.apply(N, argsOverrided))
        // eslint-disable-next-line functional/functional-parameters
        .catch(() => {
        const retryArgs = opts.mutation && opts.overrides?.fallback
            ? [...(args || []), opts.overrides.fallback]
            : args;
        return retryArgs === undefined ? method() : method.apply(N, retryArgs);
    });
    const data = opts.mutation ? res : stringify(res);
    return data;
};
// This is a sample code
// const sample = async (dwa: string) => {
// 	const res = await execute<MutationOption>({
// 		contract: {} as any,
// 		method: 'vote',
// 		args: ['dwadwa', true],
// 		mutation: true,
// 	})
// 	const receipt = await res.wait()
// 	const res2 = await execute<QueryOption>({
// 		contract: {} as any,
// 		method: 'vote',
// 		args: ['dwadwa', true],
// 		mutation: false,
// 	})
// }

const createTransferCaller = (contract) => async (to, value, overrides) => execute({
    contract,
    method: 'transfer',
    args: [to, value],
    mutation: true,
    overrides,
});

const createBalanceOfCaller = (contract) => async (address) => execute({
    contract,
    method: 'balanceOf',
    args: [address],
    mutation: false,
});

const createTotalSupplyCaller = (contract) => always(execute({
    contract,
    method: 'totalSupply',
    mutation: false,
}));

const createApproveCaller = (contract) => async (to, value, overrides) => execute({
    contract,
    method: 'approve',
    mutation: true,
    args: [to, value],
    overrides,
});

const createTransferFromCaller = (contract) => async (from, to, value, overrides) => execute({
    contract,
    method: 'transferFrom',
    mutation: true,
    args: [from, to, value],
    overrides,
});

const createNameCaller = (contract) => always(execute({
    contract,
    method: 'name',
    mutation: false,
}));

const createSymbolCaller = (contract) => always(execute({
    contract,
    method: 'symbol',
    mutation: false,
}));

const createDecimalsCaller = (contract) => always(execute({
    contract,
    method: 'decimals',
    mutation: false,
}));

const createAllowanceCaller = (contract) => async (from, to) => execute({
    contract,
    method: 'allowance',
    args: [from, to],
    mutation: false,
});

const createErc20Contract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...erc20Abi], provider);
    return {
        totalSupply: createTotalSupplyCaller(contract),
        balanceOf: createBalanceOfCaller(contract),
        transfer: createTransferCaller(contract),
        allowance: createAllowanceCaller(contract),
        approve: createApproveCaller(contract),
        transferFrom: createTransferFromCaller(contract),
        name: createNameCaller(contract),
        symbol: createSymbolCaller(contract),
        decimals: createDecimalsCaller(contract),
    };
};

const arrayify = (obj) => {
    const keys = Object.keys(obj);
    const numericKeys = keys.filter((k) => !isNaN(Number(k)));
    return numericKeys.map((k) => obj[k]);
};

const utils = {
    execute,
    arrayify,
};

const marketAddresses = {
    eth: {
        main: {
            github: '0x34A7AdC94C4D41C3e3469F98033B372cB2fAf318',
            youtube: '0x2AD380927F509A1d6338b89A7286cFAc52a896Dd',
        },
        ropsten: {
            github: '0x1A45BA7BB4a5A4a01Eb5330C9F2fd8af47C16eBe',
        },
    },
    arbitrum: {
        one: {
            github: '0x84b9e407e2Ee76A641b45d007bBFd9e60a13FF7d',
            youtube: '0x61999A2F3f403957315255D08A969602f19F2Dcf',
            discord: '0x0f02e7c1bc8769B56B20E03d2Fe277b42194C260',
        },
        rinkeby: {
            github: '0xeb85170bce4ea8a9ca0fb5b6620ab74ef111a50c',
            youtube: '0x53f8A1DEe0aF272d995aF1e206116172c00f2eb4',
            discord: '0xC3af4341f71EfD7F74523D4cD9eD87F36Ed75A99',
        },
    },
    polygon: {
        mainnet: {
            github: '0xa7d0A25Cb2378c935FF94863C8614367b9e85Ed8',
            youtube: '0xfe607787e14A90A320cdfc05710b382380B4fd12',
            discord: '0x75b2C22E5f1EF64814D574caa9c4506Eba268502',
        },
        mumbai: {
            github: '0x9edc758B0477559AB6556BCA9971B4DD892d8E28',
            youtube: '0x7C7CDc82b195d4dcf9a789BD14CD1C73232025a8',
            discord: '0xb5A7CFb9F495De44e58177990dac004b84e7C501',
        },
    },
};

const marketAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_behavior',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        constant: true,
        inputs: [],
        name: 'behavior',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'enabled',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'issuedMetrics',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'votingEndBlockNumber',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'toEnable',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_prop',
                type: 'address',
            },
            {
                internalType: 'string',
                name: '_args1',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args2',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args3',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args4',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args5',
                type: 'string',
            },
        ],
        name: 'authenticate',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_prop',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_author',
                type: 'address',
            },
            {
                internalType: 'string',
                name: '_args1',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args2',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args3',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args4',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args5',
                type: 'string',
            },
        ],
        name: 'authenticateFromPropertyFactory',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'bytes32',
                name: '_idHash',
                type: 'bytes32',
            },
        ],
        name: 'authenticatedCallback',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'deauthenticate',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'schema',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
];

const createSchemaCaller = (contract) => always(execute({ contract, method: 'schema', mutation: false }).then((result) => JSON.parse(result
    .split(`'`)
    .map((v) => ({ v, s: /[[\],:]/.test(v) }))
    .reduce((a, c) => `${a}${c.s ? c.v : `"${c.v}"`}`, ''))));

const createVoteCaller = (contract) => async (propertyAddress, agree, overrides) => execute({
    contract,
    method: 'vote',
    args: [propertyAddress, agree],
    mutation: true,
    overrides,
});

const metricsAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        constant: true,
        inputs: [],
        name: 'market',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'property',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
];

const metricsFactoryAbi$1 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'Destroy',
        type: 'event',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'destroy',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

/* eslint-disable functional/no-throw-statement */
/* eslint-disable functional/no-conditional-statement */
/* eslint-disable no-constant-condition */
/* eslint-disable functional/no-loop-statement */
/* eslint-disable functional/no-expression-statement */
/* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
const getMetricsProperty = async (address, provider) => execute({
    contract: new ethers.Contract(address, metricsAbi, provider),
    mutation: false,
    method: 'property',
});
const createAuthenticateCaller = (contract, provider) => async (propertyAddress, args, { metricsFactoryAddress }, overrides) => {
    await execute({
        contract,
        method: 'authenticate',
        mutation: true,
        args: [propertyAddress, ...args],
        padEnd: 6,
        overrides,
    });
    const metricsFactoryContract = new ethers.Contract(metricsFactoryAddress, metricsFactoryAbi$1, provider);
    return new Promise((resolve, reject) => {
        const subscriberdContract = metricsFactoryContract.on('Create', async (_, metricsAddress) => getMetricsProperty(metricsAddress, provider)
            .then(async (metricsProperty) => {
            if (metricsProperty === propertyAddress) {
                (await subscriberdContract).removeAllListeners();
                resolve(metricsAddress);
            }
        })
            .catch(reject));
    });
};

/* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
const createBehaviorCaller = (contract) => always(execute({
    contract,
    method: 'behavior',
    mutation: false,
}));

const createMarketContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...marketAbi], provider);
    return {
        vote: createVoteCaller(contract),
        schema: createSchemaCaller(contract),
        authenticate: createAuthenticateCaller(contract, provider),
        behavior: createBehaviorCaller(contract),
        // eslint-disable-next-line functional/functional-parameters
        contract: () => contract,
    };
};

const marketBehaviorAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_prop',
                type: 'address',
            },
            {
                internalType: 'string[]',
                name: '_args',
                type: 'string[]',
            },
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'authenticate',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
        ],
        name: 'setAssociatedMarket',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'associatedMarket',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'schema',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'getId',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_id',
                type: 'string',
            },
        ],
        name: 'getMetrics',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createGetIdCaller = (contract) => (metricsAddress) => execute({
    contract,
    method: 'getId',
    args: [metricsAddress],
    mutation: false,
});

const createGetMetricsCaller = (contract) => (id) => execute({
    contract,
    method: 'getMetrics',
    args: [id],
    mutation: false,
});

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const createMarketBehaviorContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...marketBehaviorAbi], provider);
    return {
        getId: createGetIdCaller(contract),
        getMetrics: createGetMetricsCaller(contract),
        contract: () => contract,
    };
};

const propertyAbi$1 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_own',
                type: 'address',
            },
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: '_old',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_new',
                type: 'address',
            },
        ],
        name: 'ChangeAuthor',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'string',
                name: '_old',
                type: 'string',
            },
            {
                indexed: false,
                internalType: 'string',
                name: '_new',
                type: 'string',
            },
        ],
        name: 'ChangeName',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'string',
                name: '_old',
                type: 'string',
            },
            {
                indexed: false,
                internalType: 'string',
                name: '_new',
                type: 'string',
            },
        ],
        name: 'ChangeSymbol',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
        ],
        name: 'allowance',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'approve',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'subtractedValue',
                type: 'uint256',
            },
        ],
        name: 'decreaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'addedValue',
                type: 'uint256',
            },
        ],
        name: 'increaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'author',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'symbol',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'decimals',
        outputs: [
            {
                internalType: 'uint8',
                name: '',
                type: 'uint8',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_nextAuthor',
                type: 'address',
            },
        ],
        name: 'changeAuthor',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
        ],
        name: 'changeName',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
        ],
        name: 'changeSymbol',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'transfer',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'transferFrom',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_sender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'withdraw',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createAuthorCaller = (contract) => always(execute({
    contract,
    method: 'author',
    mutation: false,
}));

const createChangeAuthorCaller = (contract) => async (nextAuther, overrides) => execute({
    contract,
    method: 'changeAuthor',
    mutation: true,
    args: [nextAuther],
    overrides,
});

const createChangeNameCaller = (contract) => async (nextName, overrides) => execute({
    contract,
    method: 'changeName',
    mutation: true,
    args: [nextName],
    overrides,
});

const createChangeSymbolCaller = (contract) => async (nextSymbol, overrides) => execute({
    contract,
    method: 'changeSymbol',
    mutation: true,
    args: [nextSymbol],
    overrides,
});

const createPropertyContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...propertyAbi$1], provider);
    return {
        totalSupply: createTotalSupplyCaller(contract),
        balanceOf: createBalanceOfCaller(contract),
        transfer: createTransferCaller(contract),
        allowance: createAllowanceCaller(contract),
        approve: createApproveCaller(contract),
        transferFrom: createTransferFromCaller(contract),
        name: createNameCaller(contract),
        symbol: createSymbolCaller(contract),
        decimals: createDecimalsCaller(contract),
        author: createAuthorCaller(contract),
        changeAuthor: createChangeAuthorCaller(contract),
        changeName: createChangeNameCaller(contract),
        changeSymbol: createChangeSymbolCaller(contract),
        contract: () => contract,
    };
};

const propertyFactoryAbi$1 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
            {
                internalType: 'address',
                name: '_author',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
            {
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
            {
                internalType: 'string',
                name: '_args1',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args2',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_args3',
                type: 'string',
            },
        ],
        name: 'createAndAuthenticate',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createCreatePropertyCaller = (contract) => async (name, symbol, author, overrides) => {
    await execute({
        contract,
        method: 'create',
        args: [name, symbol, author],
        mutation: true,
        overrides,
    });
    return new Promise((resolve) => {
        const subscribedContract = contract.on('Create', async (_, propertyAddress) => {
            (await subscribedContract).removeAllListeners();
            resolve(propertyAddress);
        });
    });
};

const createCreateAndAuthenticateCaller$1 = (contract, provider) => async (name, symbol, marketAddress, args, { metricsFactoryAddress }, overrides) => {
    const transaction = await execute({
        contract,
        method: 'createAndAuthenticate',
        args: [name, symbol, marketAddress, ...args],
        mutation: true,
        padEnd: 6,
        overrides,
    });
    const metricsFactoryContract = new ethers.Contract(metricsFactoryAddress, metricsFactoryAbi$1, provider);
    const waitForAuthentication = () => new Promise((resolve, reject) => {
        const subscriberdContract = metricsFactoryContract.on('Create', async (_, metricsAddress) => getMetricsProperty(metricsAddress, provider)
            .then(async (metricsProperty) => {
            if (metricsProperty === marketAddress) {
                (await subscriberdContract).removeAllListeners();
                resolve(metricsAddress);
            }
        })
            .catch(reject));
    });
    return new Promise((resolve) => {
        const subscribedContract = contract.on('Create', async (_, propertyAddress) => {
            (await subscribedContract).removeAllListeners();
            resolve({
                property: propertyAddress,
                transaction,
                waitForAuthentication,
            });
        });
    });
};

const createPropertyFactoryContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...propertyFactoryAbi$1], provider);
    return {
        create: createCreatePropertyCaller(contract),
        createAndAuthenticate: createCreateAndAuthenticateCaller$1(contract, provider),
        contract: () => contract,
    };
};

const allocatorAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'calculateMaxRewardsPerBlock',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
        ],
        name: 'beforeBalanceChange',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createCalculateMaxRewardsPerBlockCaller = (contract) => always(execute({
    contract,
    method: 'calculateMaxRewardsPerBlock',
    mutation: false,
}));

const createAllocatorContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...allocatorAbi], provider);
    return {
        calculateMaxRewardsPerBlock: createCalculateMaxRewardsPerBlockCaller(contract),
        contract: () => contract,
    };
};

const lockupAbi$1 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_devMinter',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sTokensManager',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'Lockedup',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint256',
                name: '_cap',
                type: 'uint256',
            },
        ],
        name: 'UpdateCap',
        type: 'event',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'changeOwner',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'createStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'devMinter',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageAllValue',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageCumulativeGlobalRewards',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageCumulativeHoldersRewardCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageDIP4GenesisBlock',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageFallbackInitialCumulativeHoldersRewardCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getStorageInitialCumulativeHoldersRewardCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getStorageInterestPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageLastCumulativeHoldersPriceCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getStorageLastCumulativeHoldersRewardAmountPerProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageLastCumulativeHoldersRewardPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getStorageLastCumulativeHoldersRewardPricePerProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageLastCumulativeInterestPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getStorageLastInterestPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageLastSameRewardsAmountAndBlock',
        outputs: [
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_block',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getStorageLastStakedInterestPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageLastStakesChangedCumulativeReward',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getStoragePendingInterestWithdrawal',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getStoragePropertyValue',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sender',
                type: 'address',
            },
        ],
        name: 'getStorageValue',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'isOwner',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'sTokensManager',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_storageAddress',
                type: 'address',
            },
        ],
        name: 'setStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'depositToProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
        ],
        name: 'depositToProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'depositToPosition',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'lockup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'withdrawByPosition',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'withdraw',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'cap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'uint256',
                name: '_cap',
                type: 'uint256',
            },
        ],
        name: 'updateCap',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'calculateCumulativeRewardPrices',
        outputs: [
            {
                internalType: 'uint256',
                name: '_reward',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_holders',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_interest',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_holdersCap',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'calculateCumulativeHoldersRewardAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'calculateRewardAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'update',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'calculateWithdrawableInterestAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'calculateWithdrawableInterestAmountByPosition',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getAllValue',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sender',
                type: 'address',
            },
        ],
        name: 'getValue',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getPropertyValue',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: '___setFallbackInitialCumulativeHoldersRewardCap',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'migrateToSTokens',
        outputs: [
            {
                internalType: 'uint256',
                name: 'tokenId_',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createGetValueCaller = (contract) => async (propertyAddress, accountAddress) => execute({
    contract,
    method: 'getValue',
    args: [propertyAddress, accountAddress],
    mutation: false,
});

const createGetPropertyValueCaller = (contract) => async (address) => execute({
    contract,
    method: 'getPropertyValue',
    args: [address],
    mutation: false,
});

const createWithdrawCaller$2 = (contract) => async (propertyAddress, amount, overrides) => execute({
    contract,
    method: 'withdraw',
    mutation: true,
    args: [propertyAddress, amount],
    overrides,
});

const createCalculateWithdrawableInterestAmountCaller = (contract) => async (propertyAddress, account) => execute({
    contract,
    method: 'calculateWithdrawableInterestAmount',
    args: [propertyAddress, account],
    mutation: false,
});

const createGetAllValueCaller = (contract) => always(execute({
    contract,
    method: 'getAllValue',
    mutation: false,
}));

const createGetStorageWithdrawalStatusCaller = (contract) => async (propertyAddress, accountAddress) => execute({
    contract,
    method: 'getStorageWithdrawalStatus',
    args: [propertyAddress, accountAddress],
    mutation: false,
});

const createCalculateCumulativeHoldersRewardAmountCaller = (contract) => async (propertyAddress) => execute({
    contract,
    method: 'calculateCumulativeHoldersRewardAmount',
    args: [propertyAddress],
    mutation: false,
});

const createCalculateCumulativeRewardPricesCaller = (contract) => always(execute({
    contract,
    method: 'calculateCumulativeRewardPrices',
    mutation: false,
}).then((r) => arrayify(r)));

const createCalculateRewardAmountCaller = (contract) => async (propertyAddress) => execute({
    contract,
    method: 'calculateRewardAmount',
    args: [propertyAddress],
    mutation: false,
}).then((r) => arrayify(r));

const createCapCaller = (contract) => always(execute({
    contract,
    method: 'cap',
    mutation: false,
}));

const createWithdrawByPositionCaller = (contract) => async (positionTokenId, amount, overrides) => execute({
    contract,
    method: 'withdrawByPosition',
    mutation: true,
    args: [positionTokenId, amount],
    overrides,
});

const createcalculateWithdrawableInterestAmountByPositionCaller = (contract) => async (positionTokenId) => execute({
    contract,
    method: 'calculateWithdrawableInterestAmountByPosition',
    args: [positionTokenId],
    mutation: false,
});

const createDepositToPropertyCaller = (contract) => async (propertyAddress, amount, payload, overrides) => execute({
    contract,
    method: 'depositToProperty',
    mutation: true,
    args: payload
        ? [propertyAddress, amount, payload]
        : [propertyAddress, amount],
    overrides,
    interface: payload ? 'address,uint256,bytes32' : 'address,uint256',
});

const createDepositToPositionCaller = (contract) => async (positionTokenId, amount, overrides) => execute({
    contract,
    method: 'depositToPosition',
    mutation: true,
    args: [positionTokenId, amount],
    overrides,
});

const createMigrateToSTokensCaller = (contract) => async (propertyAddress, overrides) => execute({
    contract,
    method: 'migrateToSTokens',
    mutation: true,
    args: [propertyAddress],
    overrides,
});

const createLockupContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...lockupAbi$1], provider);
    return {
        getValue: createGetValueCaller(contract),
        getAllValue: createGetAllValueCaller(contract),
        getPropertyValue: createGetPropertyValueCaller(contract),
        withdrawByPosition: createWithdrawByPositionCaller(contract),
        withdraw: createWithdrawCaller$2(contract),
        calculateWithdrawableInterestAmountByPosition: createcalculateWithdrawableInterestAmountByPositionCaller(contract),
        calculateWithdrawableInterestAmount: createCalculateWithdrawableInterestAmountCaller(contract),
        calculateCumulativeHoldersRewardAmount: createCalculateCumulativeHoldersRewardAmountCaller(contract),
        getStorageWithdrawalStatus: createGetStorageWithdrawalStatusCaller(contract),
        calculateCumulativeRewardPrices: createCalculateCumulativeRewardPricesCaller(contract),
        calculateRewardAmount: createCalculateRewardAmountCaller(contract),
        cap: createCapCaller(contract),
        depositToProperty: createDepositToPropertyCaller(contract),
        depositToPosition: createDepositToPositionCaller(contract),
        migrateToSTokens: createMigrateToSTokensCaller(contract),
        contract: () => contract,
    };
};

const devAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'MinterAdded',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'MinterRemoved',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'addMinter',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
        ],
        name: 'allowance',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'approve',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'burn',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'burnFrom',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'decimals',
        outputs: [
            {
                internalType: 'uint8',
                name: '',
                type: 'uint8',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'subtractedValue',
                type: 'uint256',
            },
        ],
        name: 'decreaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'addedValue',
                type: 'uint256',
            },
        ],
        name: 'increaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'isMinter',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'mint',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceMinter',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'symbol',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'recipient',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'transfer',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'sender',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'recipient',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'transferFrom',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'deposit',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'depositFrom',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'fee',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createDepositCaller = (contract) => async (to, value, overrides) => execute({
    contract,
    method: 'deposit',
    args: [to, value],
    mutation: true,
    overrides,
});

const createDevContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...devAbi], provider);
    return {
        totalSupply: createTotalSupplyCaller(contract),
        balanceOf: createBalanceOfCaller(contract),
        transfer: createTransferCaller(contract),
        allowance: createAllowanceCaller(contract),
        approve: createApproveCaller(contract),
        transferFrom: createTransferFromCaller(contract),
        name: createNameCaller(contract),
        symbol: createSymbolCaller(contract),
        decimals: createDecimalsCaller(contract),
        deposit: createDepositCaller(contract),
        contract: () => contract,
    };
};

const withdrawAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_devMinter',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
        ],
        name: 'PropertyTransfer',
        type: 'event',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'changeOwner',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'createStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'devMinter',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getCumulativePrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getLastWithdrawalPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getPendingWithdrawal',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getRewardsAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getStorageLastWithdrawnReward',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'getStorageLastWithdrawnRewardCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'isOwner',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_storageAddress',
                type: 'address',
            },
        ],
        name: 'setStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'withdraw',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
        ],
        name: 'beforeBalanceChange',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'calculateWithdrawableAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_user',
                type: 'address',
            },
        ],
        name: 'calculateRewardAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_price',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_cap',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_allReward',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
];

const createWithdrawCaller$1 = (contract) => async (propertyAddress, overrides) => execute({
    contract,
    method: 'withdraw',
    mutation: true,
    args: [propertyAddress],
    overrides,
});

const createGetRewardsAmountCaller = (contract) => async (address) => execute({
    contract,
    method: 'getRewardsAmount',
    args: [address],
    mutation: false,
});

const createCalculateWithdrawableAmountCaller = (contract) => async (propertyAddress, accountAddress) => execute({
    contract,
    method: 'calculateWithdrawableAmount',
    args: [propertyAddress, accountAddress],
    mutation: false,
});

const createBulkWithdrawCaller = (contract) => async (propertyAddresses) => execute({
    contract,
    method: 'bulkWithdraw',
    mutation: true,
    args: propertyAddresses,
});

const calculateRewardAmountCaller = (contract) => async (propertyAddress, accountAddress) => execute({
    contract,
    method: 'calculateRewardAmount',
    args: [propertyAddress, accountAddress],
    mutation: false,
});

const createWithdrawContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...withdrawAbi], provider);
    return {
        withdraw: createWithdrawCaller$1(contract),
        bulkWithdraw: createBulkWithdrawCaller(contract),
        getRewardsAmount: createGetRewardsAmountCaller(contract),
        calculateWithdrawableAmount: createCalculateWithdrawableAmountCaller(contract),
        calculateRewardAmount: calculateRewardAmountCaller(contract),
        contract: () => contract,
    };
};

const addressConfigAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        constant: true,
        inputs: [],
        name: '_owner',
        outputs: [
            {
                internalType: 'address payable',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'allocator',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'allocatorStorage',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'isOwner',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'kill',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'lockup',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'lockupStorage',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'marketFactory',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'marketGroup',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'metricsFactory',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'metricsGroup',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'policy',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'policyFactory',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'policyGroup',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'policySet',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'propertyFactory',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'propertyGroup',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'token',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'voteCounter',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'voteCounterStorage',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'voteTimes',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'voteTimesStorage',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'withdraw',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'withdrawStorage',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setAllocator',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setAllocatorStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setWithdraw',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setWithdrawStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setMarketFactory',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setMarketGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setPropertyFactory',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setPropertyGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setMetricsFactory',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setMetricsGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setPolicyFactory',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setPolicyGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setPolicySet',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setPolicy',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setToken',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setLockup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setLockupStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setVoteTimes',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setVoteTimesStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setVoteCounter',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setVoteCounterStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createTokenCaller = (contract) => always(execute({
    contract,
    method: 'token',
    mutation: false,
}));

const createAllocatorCaller = (contract) => always(execute({
    contract,
    method: 'allocator',
    mutation: false,
}));

const createAllocatorStorageCaller = (contract) => always(execute({
    contract,
    method: 'allocatorStorage',
    mutation: false,
}));

const createLockupCaller = (contract) => always(execute({
    contract,
    method: 'lockup',
    mutation: false,
}));

const createLockupStorageCaller = (contract) => always(execute({
    contract,
    method: 'lockupStorage',
    mutation: false,
}));

const createMarketFactoryCaller = (contract) => always(execute({
    contract,
    method: 'marketFactory',
    mutation: false,
}));

const createMarketGroupCaller = (contract) => always(execute({
    contract,
    method: 'marketGroup',
    mutation: false,
}));

const createMetricsFactoryCaller = (contract) => always(execute({
    contract,
    method: 'metricsFactory',
    mutation: false,
}));

const createMetricsGroupCaller = (contract) => always(execute({
    contract,
    method: 'metricsGroup',
    mutation: false,
}));

const createPolicyCaller = (contract) => always(execute({
    contract,
    method: 'policy',
    mutation: false,
}));

const createPropertyFactoryCaller = (contract) => always(execute({
    contract,
    method: 'propertyFactory',
    mutation: false,
}));

const createPropertyGroupCaller = (contract) => always(execute({
    contract,
    method: 'propertyGroup',
    mutation: false,
}));

const createWithdrawCaller = (contract) => always(execute({
    contract,
    method: 'withdraw',
    mutation: false,
}));

const createWithdrawStorageCaller = (contract) => always(execute({
    contract,
    method: 'withdrawStorage',
    mutation: false,
}));

const createPolicyFactoryCaller = (contract) => always(execute({
    contract,
    method: 'policyFactory',
    mutation: false,
}));

const createPolicySetCaller = (contract) => always(execute({
    contract,
    method: 'policySet',
    mutation: false,
}));

const createPolicyGroupCaller = (contract) => always(execute({
    contract,
    method: 'policyGroup',
    mutation: false,
}));

const createRegistryContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...addressConfigAbi], provider);
    return {
        allocator: createAllocatorCaller(contract),
        allocatorStorage: createAllocatorStorageCaller(contract),
        lockup: createLockupCaller(contract),
        lockupStorage: createLockupStorageCaller(contract),
        marketFactory: createMarketFactoryCaller(contract),
        marketGroup: createMarketGroupCaller(contract),
        metricsFactory: createMetricsFactoryCaller(contract),
        metricsGroup: createMetricsGroupCaller(contract),
        policy: createPolicyCaller(contract),
        policySet: createPolicySetCaller(contract),
        policyGroup: createPolicyGroupCaller(contract),
        policyFactory: createPolicyFactoryCaller(contract),
        propertyFactory: createPropertyFactoryCaller(contract),
        propertyGroup: createPropertyGroupCaller(contract),
        token: createTokenCaller(contract),
        withdraw: createWithdrawCaller(contract),
        withdrawStorage: createWithdrawStorageCaller(contract),
        contract: () => contract,
    };
};

const policyAbi$1 = [
    {
        constant: true,
        inputs: [
            {
                internalType: 'uint256',
                name: '_lockups',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_assets',
                type: 'uint256',
            },
        ],
        name: 'rewards',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_lockups',
                type: 'uint256',
            },
        ],
        name: 'holdersShare',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'uint256',
                name: '_assets',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_propertyAssets',
                type: 'uint256',
            },
        ],
        name: 'authenticationFee',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'marketVotingBlocks',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'policyVotingBlocks',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'uint256',
                name: '_supply',
                type: 'uint256',
            },
        ],
        name: 'shareOfTreasury',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'treasury',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'capSetter',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
];

const createHoldersShareCaller = (contract) => async (amount, lockups) => execute({
    contract,
    method: 'holdersShare',
    args: [amount, lockups],
    mutation: false,
});

const createRewardsCaller$1 = (contract) => async (lockups, assets) => execute({
    contract,
    method: 'rewards',
    args: [lockups, assets],
    mutation: false,
});

const createAuthenticationFeeCaller = (contract) => async (assets, propertyAssets) => execute({
    contract,
    method: 'authenticationFee',
    args: [assets, propertyAssets],
    mutation: false,
});

const createMarketVotingBlocksCaller = (contract) => always(execute({
    contract,
    method: 'marketVotingBlocks',
    mutation: false,
}));

const createPolicyVotingBlocksCaller = (contract) => always(execute({
    contract,
    method: 'policyVotingBlocks',
    mutation: false,
}));

const createShareOfTreasuryCaller = (contract) => async (supply) => execute({
    contract,
    method: 'shareOfTreasury',
    args: [supply],
    mutation: false,
});

const createTreasuryCaller = (contract) => always(execute({
    contract,
    method: 'treasury',
    mutation: false,
}));

const createCapSetterCaller = (contract) => always(execute({
    contract,
    method: 'capSetter',
    mutation: false,
}));

const createPolicyContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...policyAbi$1], provider);
    return {
        holdersShare: createHoldersShareCaller(contract),
        rewards: createRewardsCaller$1(contract),
        authenticationFee: createAuthenticationFeeCaller(contract),
        marketVotingBlocks: createMarketVotingBlocksCaller(contract),
        policyVotingBlocks: createPolicyVotingBlocksCaller(contract),
        shareOfTreasury: createShareOfTreasuryCaller(contract),
        treasury: createTreasuryCaller(contract),
        capSetter: createCapSetterCaller(contract),
        contract: () => contract,
    };
};

const policyGroupAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'changeOwner',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'createStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'getStorageAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'isOwner',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_storageAddress',
                type: 'address',
            },
        ],
        name: 'setStorage',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'addGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'isGroup',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_policy',
                type: 'address',
            },
        ],
        name: 'isDuringVotingPeriod',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
];

const createIsGroupCaller = (contract) => async (policyAddress) => execute({
    contract,
    method: 'isGroup',
    args: [policyAddress],
    mutation: false,
}).then(T);

const createPolicyGroupContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...policyGroupAbi], provider);
    return {
        isGroup: createIsGroupCaller(contract),
        isDuringVotingPeriod: createIsGroupCaller(contract),
        contract: () => contract,
    };
};

const createPropertyCaller = (contract) => always(execute({
    contract,
    method: 'property',
    mutation: false,
}));

const createMarketCaller = (contract) => always(execute({
    contract,
    method: 'market',
    mutation: false,
}));

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const createMetricsContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...metricsAbi], provider);
    return {
        property: createPropertyCaller(contract),
        market: createMarketCaller(contract),
        contract: () => contract,
    };
};

const policyFactoryAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_policy',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'isOwner',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_newPolicyAddress',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_policy',
                type: 'address',
            },
        ],
        name: 'forceAttach',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createCreateCaller$1 = (contract) => async (newPolicyAddress, overrides) => execute({
    contract,
    method: 'create',
    mutation: true,
    args: [newPolicyAddress],
    overrides,
});

const createForceAttachCaller = (contract) => async (policy, overrides) => execute({
    contract,
    method: 'forceAttach',
    mutation: true,
    args: [policy],
    overrides,
});

const createPolicyFactoryContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...policyFactoryAbi], provider);
    return {
        create: createCreateCaller$1(contract),
        forceAttach: createForceAttachCaller(contract),
        contract: () => contract,
    };
};

const sTokensAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'approved',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'bool',
                name: 'approved',
                type: 'bool',
            },
        ],
        name: 'ApprovalForAll',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'address',
                name: 'freezingUser',
                type: 'address',
            },
        ],
        name: 'Freezed',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: 'property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'price',
                type: 'uint256',
            },
        ],
        name: 'Minted',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'price',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'cumulativeReward',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'pendingReward',
                type: 'uint256',
            },
        ],
        name: 'Updated',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'approve',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'descriptorOf',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        name: 'descriptorOfPropertyByPayload',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'getApproved',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
        ],
        name: 'isApprovedForAll',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        name: 'isFreezed',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'ownerOf',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        name: 'payloadOf',
        outputs: [
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'royaltyOf',
        outputs: [
            {
                internalType: 'uint24',
                name: '',
                type: 'uint24',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                internalType: 'bytes',
                name: '_data',
                type: 'bytes',
            },
        ],
        name: 'safeTransferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'operator',
                type: 'address',
            },
            {
                internalType: 'bool',
                name: 'approved',
                type: 'bool',
            },
        ],
        name: 'setApprovalForAll',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'symbol',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
        ],
        name: 'transferFrom',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes4',
                name: 'interfaceId',
                type: 'bytes4',
            },
        ],
        name: 'supportsInterface',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_owner',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'index',
                type: 'uint256',
            },
        ],
        name: 'tokenOfOwnerByIndex',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'index',
                type: 'uint256',
            },
        ],
        name: 'tokenByIndex',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_proxyAdmin',
                type: 'address',
            },
        ],
        name: 'setProxyAdmin',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'tokenURI',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: '_owner',
                type: 'address',
            },
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'amount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'price',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'pendingReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManager.StakingPositions',
                name: '_positions',
                type: 'tuple',
            },
            {
                components: [
                    {
                        internalType: 'uint256',
                        name: 'entireReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'withdrawableReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManager.Rewards',
                name: '_rewardsArg',
                type: 'tuple',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
        ],
        name: 'tokenURISim',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'currentIndex',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_price',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
        ],
        name: 'mint',
        outputs: [
            {
                internalType: 'uint256',
                name: 'tokenId_',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_price',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_cumulativeReward',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_pendingReward',
                type: 'uint256',
            },
        ],
        name: 'update',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'string',
                name: '_data',
                type: 'string',
            },
        ],
        name: 'setTokenURIImage',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_descriptor',
                type: 'address',
            },
            {
                internalType: 'bytes32[]',
                name: '_keys',
                type: 'bytes32[]',
            },
        ],
        name: 'setTokenURIDescriptor',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_descriptor',
                type: 'address',
            },
        ],
        name: 'setTokenURIDescriptor',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'freezeTokenURI',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'positions',
        outputs: [
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'amount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'price',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'pendingReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManager.StakingPositions',
                name: '',
                type: 'tuple',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'rewards',
        outputs: [
            {
                components: [
                    {
                        internalType: 'uint256',
                        name: 'entireReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'withdrawableReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManager.Rewards',
                name: '',
                type: 'tuple',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'positionsOfProperty',
        outputs: [
            {
                internalType: 'uint256[]',
                name: '',
                type: 'uint256[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_owner',
                type: 'address',
            },
        ],
        name: 'positionsOfOwner',
        outputs: [
            {
                internalType: 'uint256[]',
                name: '',
                type: 'uint256[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_percentage',
                type: 'uint256',
            },
        ],
        name: 'setSTokenRoyaltyForProperty',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'tokenId',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'royaltyInfo',
        outputs: [
            {
                internalType: 'address',
                name: 'receiver',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'royaltyAmount',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
];

const createPositionsCaller = (contract) => async (tokenId) => {
    const res = await execute({
        contract,
        method: 'positions',
        args: [String(tokenId)],
        mutation: false,
    });
    const arrayified = arrayify(res);
    return {
        property: arrayified[0],
        amount: arrayified[1],
        price: arrayified[2],
        cumulativeReward: arrayified[3],
        pendingReward: arrayified[4],
    };
};

const createIsFreezedCaller = (contract) => async (tokenId) => execute({
    contract,
    method: 'isFreezed',
    args: [String(tokenId)],
    mutation: false,
}).then(T);

const createFreezeTokenURICaller = (contract) => async (tokenId, overrides) => execute({
    contract,
    method: 'freezeTokenURI',
    mutation: true,
    args: [String(tokenId)],
    overrides,
});

const createSetTokenURIImageCaller = (contract) => async (tokenId, data, overrides) => execute({
    contract,
    method: 'setTokenURIImage',
    mutation: true,
    args: [String(tokenId), data],
    overrides,
});

const createOwnerOfCaller = (contract) => async (tokenId) => execute({
    contract,
    method: 'ownerOf',
    args: [String(tokenId)],
    mutation: false,
});

const createRewardsCaller = (contract) => async (tokenId) => {
    const res = await execute({
        contract,
        method: 'rewards',
        args: [String(tokenId)],
        mutation: false,
    });
    const arrayified = arrayify(res);
    return {
        entireReward: arrayified[0],
        cumulativeReward: arrayified[1],
        withdrawableReward: arrayified[2],
    };
};

/* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
const createTokenURICaller = (contract) => async (tokenId) => {
    const res = await execute({
        contract,
        method: 'tokenURI',
        args: [String(tokenId)],
        mutation: false,
    });
    const decoded = decode(res.replace(/^data:application\/json;base64,(.*)/, '$1')).replace(/\n/g, '\\n');
    return JSON.parse(decoded);
};

const createPayloadOfCaller = (contract) => async (tokenId) => {
    const res = execute({
        contract,
        method: 'payloadOf',
        args: [String(tokenId)],
        mutation: false,
    });
    return res;
};

const createPositionsOfPropertyCaller = (contract) => async (propertyAddress) => {
    const res = await execute({
        contract,
        method: 'positionsOfProperty',
        args: [propertyAddress],
        mutation: false,
    });
    return res.map(Number);
};

const createPositionsOfOwnerCaller = (contract) => async (accountAddress) => {
    const res = await execute({
        contract,
        method: 'positionsOfOwner',
        args: [accountAddress],
        mutation: false,
    });
    return res.map(Number);
};

const createSetSTokenRoyaltyForPropertyCaller = (contract) => async (propertyAddress, royalty) => execute({
    contract,
    method: 'setSTokenRoyaltyForProperty',
    mutation: true,
    args: [propertyAddress, String(royalty)],
});

const createRoyaltyOfCaller = (contract) => async (propertyAddress) => {
    const res = execute({
        contract,
        method: 'royaltyOf',
        args: [propertyAddress],
        mutation: false,
    });
    return res;
};

const createSetTokenURIDescriptorCaller = (contract) => async (propertyAddress, descriptorAddress, payloads, overrides) => execute({
    contract,
    method: 'setTokenURIDescriptor',
    mutation: true,
    args: payloads
        ? [
            propertyAddress,
            descriptorAddress,
            payloads.map((p) => (p instanceof Uint8Array ? keccak256(p) : p)),
        ]
        : [propertyAddress, descriptorAddress],
    overrides,
    interface: payloads ? 'address,address,bytes32[]' : 'address,address',
});

/* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
const defaultPositions = {
    property: ZeroAddress,
    amount: '0',
    price: '0',
    cumulativeReward: '0',
    pendingReward: '0',
};
const defaultRewards = {
    entireReward: '0',
    cumulativeReward: '0',
    withdrawableReward: '0',
};
const createTokenURISimCaller = (contract) => async (props = {}) => {
    const tokenId = String(props.tokenId ?? 0);
    const owner = props.owner ?? ZeroAddress;
    const positions = { ...defaultPositions, ...props.positions };
    const rewards = { ...defaultRewards, ...props.rewards };
    const payload = props.payload ? props.payload : ZeroHash;
    const res = await execute({
        contract,
        method: 'tokenURISim',
        args: [tokenId, owner, values(positions), values(rewards), payload],
        mutation: false,
    });
    const decoded = decode(res.replace(/^data:application\/json;base64,(.*)/, '$1')).replace(/\n/g, '\\n');
    return JSON.parse(decoded);
};

const createDescriptorOfCaller = (contract) => async (propertyAddress) => {
    const res = execute({
        contract,
        method: 'descriptorOf',
        args: [propertyAddress],
        mutation: false,
    });
    return res;
};

const createDescriptorOfPropertyByPayloadCaller = (contract) => async (propertyAddress, payload) => {
    const res = execute({
        contract,
        method: 'descriptorOfPropertyByPayload',
        args: [propertyAddress, payload],
        mutation: false,
    });
    return res;
};

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const createSTokensContract = (provider) => (address) => {
    const contractClient = new ethers.Contract(address, [...sTokensAbi], provider);
    return {
        positions: createPositionsCaller(contractClient),
        isFreezed: createIsFreezedCaller(contractClient),
        freezeTokenURI: createFreezeTokenURICaller(contractClient),
        setTokenURIImage: createSetTokenURIImageCaller(contractClient),
        setSTokenRoyaltyForProperty: createSetSTokenRoyaltyForPropertyCaller(contractClient),
        royaltyOf: createRoyaltyOfCaller(contractClient),
        setTokenURIDescriptor: createSetTokenURIDescriptorCaller(contractClient),
        ownerOf: createOwnerOfCaller(contractClient),
        rewards: createRewardsCaller(contractClient),
        tokenURI: createTokenURICaller(contractClient),
        tokenURISim: createTokenURISimCaller(contractClient),
        payloadOf: createPayloadOfCaller(contractClient),
        positionsOfProperty: createPositionsOfPropertyCaller(contractClient),
        positionsOfOwner: createPositionsOfOwnerCaller(contractClient),
        descriptorOf: createDescriptorOfCaller(contractClient),
        descriptorOfPropertyByPayload: createDescriptorOfPropertyByPayloadCaller(contractClient),
        contract: () => contractClient,
    };
};

const metricsGroupAbi = [
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'addGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'removeGroup',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'isGroup',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'totalIssuedMetrics',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'hasAssets',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'getMetricsCountPerProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'totalAuthenticatedProperties',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'setTotalAuthenticatedPropertiesAdmin',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createTotalAuthenticatedPropertiesCaller = (contract) => always(execute({
    contract,
    method: 'totalAuthenticatedProperties',
    mutation: false,
}));

const createTotalIssuedMetrics = (contract) => always(execute({
    contract,
    method: 'totalIssuedMetrics',
    mutation: false,
}));

const createMetricsGroupContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...metricsGroupAbi], provider);
    return {
        totalAuthenticatedProperties: createTotalAuthenticatedPropertiesCaller(contract),
        totalIssuedMetrics: createTotalIssuedMetrics(contract),
        contract: () => contract,
    };
};

const createQueryCaller = (contract, method) => async (id, address, stakingPositions, rewards, keys) => execute({
    contract,
    method: method,
    args: [String(id), address, stakingPositions, rewards, keys],
    mutation: false,
});

const simpleCollectionsAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint8',
                name: 'version',
                type: 'uint8',
            },
        ],
        name: 'Initialized',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'amount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'price',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'pendingReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.StakingPositions',
                name: '_positions',
                type: 'tuple',
            },
            {
                components: [
                    {
                        internalType: 'uint256',
                        name: 'entireReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'withdrawableReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.Rewards',
                name: '',
                type: 'tuple',
            },
            {
                internalType: 'bytes32',
                name: 'key',
                type: 'bytes32',
            },
        ],
        name: 'description',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'amount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'price',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'pendingReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.StakingPositions',
                name: '_positions',
                type: 'tuple',
            },
            {
                components: [
                    {
                        internalType: 'uint256',
                        name: 'entireReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'withdrawableReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.Rewards',
                name: '',
                type: 'tuple',
            },
            {
                internalType: 'bytes32',
                name: 'key',
                type: 'bytes32',
            },
        ],
        name: 'image',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_contract',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'amount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'price',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'pendingReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.StakingPositions',
                name: '_positions',
                type: 'tuple',
            },
            {
                components: [
                    {
                        internalType: 'uint256',
                        name: 'entireReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'withdrawableReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.Rewards',
                name: '',
                type: 'tuple',
            },
            {
                internalType: 'bytes32',
                name: 'key',
                type: 'bytes32',
            },
        ],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'id',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'amount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'price',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'cumulativeReward',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'pendingReward',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ISTokensManagerStruct.StakingPositions',
                name: '_positions',
                type: 'tuple',
            },
            {
                internalType: 'bytes32',
                name: 'key',
                type: 'bytes32',
            },
        ],
        name: 'onBeforeMint',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                internalType: 'bytes32',
                name: '',
                type: 'bytes32',
            },
        ],
        name: 'propertyImages',
        outputs: [
            {
                internalType: 'string',
                name: 'src',
                type: 'string',
            },
            {
                internalType: 'string',
                name: 'name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: 'description',
                type: 'string',
            },
            {
                internalType: 'uint256',
                name: 'requiredETHAmount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'requiredETHFee',
                type: 'uint256',
            },
            {
                internalType: 'address',
                name: 'gateway',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_propertyAddress',
                type: 'address',
            },
            {
                internalType: 'bytes32',
                name: '_key',
                type: 'bytes32',
            },
        ],
        name: 'removeImage',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_propertyAddress',
                type: 'address',
            },
            {
                components: [
                    {
                        internalType: 'string',
                        name: 'src',
                        type: 'string',
                    },
                    {
                        internalType: 'string',
                        name: 'name',
                        type: 'string',
                    },
                    {
                        internalType: 'string',
                        name: 'description',
                        type: 'string',
                    },
                    {
                        internalType: 'uint256',
                        name: 'requiredETHAmount',
                        type: 'uint256',
                    },
                    {
                        internalType: 'uint256',
                        name: 'requiredETHFee',
                        type: 'uint256',
                    },
                    {
                        internalType: 'address',
                        name: 'gateway',
                        type: 'address',
                    },
                ],
                internalType: 'struct SimpleCollections.Image[]',
                name: '_images',
                type: 'tuple[]',
            },
            {
                internalType: 'bytes32[]',
                name: '_keys',
                type: 'bytes32[]',
            },
        ],
        name: 'setImages',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_contract',
                type: 'address',
            },
        ],
        name: 'setSwapAndStake',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        name: 'stakedAmountAtMinted',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'swapAndStake',
        outputs: [
            {
                internalType: 'contract ISwapAndStake',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createSetImagesCaller = (contract) => async (propertyAddress, images, keys, overrides) => execute({
    contract,
    method: 'setImages',
    mutation: true,
    args: [propertyAddress, images, keys],
    overrides,
});

const createRemoveImageCaller = (contract) => async (propertyAddress, keys, overrides) => execute({
    contract,
    method: 'removeImage',
    mutation: true,
    args: [propertyAddress, keys],
    overrides,
});

const createSimpleCollectionsContract = (provider) => (address) => {
    const contractClient = new ethers.Contract(address, [...simpleCollectionsAbi], provider);
    return {
        image: createQueryCaller(contractClient, 'image'),
        name: createQueryCaller(contractClient, 'name'),
        description: createQueryCaller(contractClient, 'description'),
        setImages: createSetImagesCaller(contractClient),
        removeImage: createRemoveImageCaller(contractClient),
    };
};

/* eslint-disable functional/no-throw-statement */
/* eslint-disable functional/no-conditional-statement */
const createDevkitContract = (provider) => ({
    allocator: createAllocatorContract(provider),
    market: createMarketContract(provider),
    marketBehavior: createMarketBehaviorContract(provider),
    property: createPropertyContract$1(provider),
    propertyFactory: createPropertyFactoryContract$1(provider),
    lockup: createLockupContract$1(provider),
    withdraw: createWithdrawContract(provider),
    dev: createDevContract$1(provider),
    registry: createRegistryContract$1(provider),
    policy: createPolicyContract$1(provider),
    policyGroup: createPolicyGroupContract(provider),
    metrics: createMetricsContract(provider),
    metricsGroup: createMetricsGroupContract(provider),
    policyFactory: createPolicyFactoryContract(provider),
    sTokens: createSTokensContract(provider),
    simpleCollections: createSimpleCollectionsContract(provider),
});
const contractFactory = createDevkitContract;

const diff = function (a, b) {
    return a - b;
};
const asc = sort(diff);
const createDetectSTokens = (sTokens) => async (propertyAddress, accountAddress) => {
    const [listForProperty, listForOwner] = await Promise.all([
        sTokens.positionsOfProperty(propertyAddress),
        sTokens.positionsOfOwner(accountAddress),
    ]);
    return asc(intersection(listForProperty, listForOwner));
};

const client = {
    createDetectSTokens,
};

const marketFactoryAbi$1 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_config',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        constant: true,
        inputs: [],
        name: 'configAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'isOwner',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: true,
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'view',
        type: 'function',
    },
    {
        constant: false,
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        constant: false,
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'enable',
        outputs: [],
        payable: false,
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createCreateCaller = (contract) => async (marketBehaviorAddress, overrides) => execute({
    contract,
    method: 'create',
    mutation: true,
    args: [marketBehaviorAddress],
    overrides,
});

const createMarketFactoryContract$1 = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...marketFactoryAbi$1], provider);
    return {
        create: createCreateCaller(contract),
        contract: () => contract,
    };
};

const agentAddresses = {
    eth: {
        main: {
            swap: {
                v2: '0x6400264F991de614048838a7d4B536733BCE3069',
                v3: undefined,
            },
        },
    },
    arbitrum: {
        one: {
            swap: {
                v2: undefined,
                v3: '0x60Ca7E3960e5F143DdB42B19B64F65c27cAD561d',
            },
        },
        rinkeby: {
            swap: { v2: undefined, v3: '0x4cd6B431B7A242D51B4D96aE5839a8b8f060F7A9' },
        },
    },
    polygon: {
        mainnet: {
            swap: {
                v2: undefined,
                v3: '0x9265Cf9e6Dc5B163bDB75d51661Ca8EA3b6150c4',
            },
            swapArbitraryTokens: {
                swap: '0xad7CaC908DfF8Dcd1D0d8d0FE3edD25bf339EB57',
            },
            usdc: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
            weth: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619',
        },
        mumbai: {
            swap: {
                v2: undefined,
                v3: '0x60650E5a95864b989C8b7B0357a2d4979a34c6AF',
            },
            swapArbitraryTokens: {
                swap: '0x927B51D9Edd43BFDE3586E99BfaCBE08135374AA',
            },
            usdc: '0xFEca406dA9727A25E71e732F9961F680059eF1F9',
            weth: '0x3c8d6A6420C922c88577352983aFFdf7b0F977cA',
        },
    },
};

const l1AvailableNetworks = [
    { chainId: 1, registry: addresses.eth.main.registry },
    { chainId: 3, registry: addresses.eth.ropsten.registry },
];
const l2AvailableNetworks = [
    {
        chainId: 42161,
        map: addresses.arbitrum.one,
    },
    { chainId: 421611, map: addresses.arbitrum.rinkeby },
    { chainId: 137, map: addresses.polygon.mainnet },
    { chainId: 80001, map: addresses.polygon.mumbai },
];
const AgentAvailableNetworks = [
    { chainId: 1, map: agentAddresses.eth.main },
    {
        chainId: 42161,
        map: agentAddresses.arbitrum.one,
    },
    { chainId: 421611, map: agentAddresses.arbitrum.rinkeby },
    { chainId: 137, map: agentAddresses.polygon.mainnet },
    { chainId: 80001, map: agentAddresses.polygon.mumbai },
];

const lockupAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'Lockedup',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint256',
                name: '_cap',
                type: 'uint256',
            },
        ],
        name: 'UpdateCap',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_reward',
                type: 'uint256',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'Withdrew',
        type: 'event',
    },
    {
        inputs: [],
        name: 'cap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'cumulativeGlobalReward',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'cumulativeHoldersRewardCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'initialCumulativeHoldersRewardCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'lastCumulativeHoldersPriceCap',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'lastCumulativeHoldersRewardAmountPerProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'lastCumulativeHoldersRewardPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'lastCumulativeHoldersRewardPricePerProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'lastCumulativeRewardPrice',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'lastLockedChangedCumulativeReward',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'lastSameGlobalRewardAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'lastSameGlobalRewardTimestamp',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'totalLocked',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'totalLockedForProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'depositToProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
        ],
        name: 'depositToProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'depositToPosition',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'withdrawByPosition',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'getLockedupProperties',
        outputs: [
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'property',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'value',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct ILockup.LockedupProperty[]',
                name: '',
                type: 'tuple[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_cap',
                type: 'uint256',
            },
        ],
        name: 'updateCap',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'calculateCumulativeRewardPrices',
        outputs: [
            {
                internalType: 'uint256',
                name: '_reward',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_holders',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_interest',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_holdersCap',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'calculateRewardAmount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
    {
        inputs: [],
        name: 'update',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_tokenId',
                type: 'uint256',
            },
        ],
        name: 'calculateWithdrawableInterestAmountByPosition',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
        constant: true,
    },
];

const createTotalLockedCaller = (contract) => always(execute({ contract, method: 'totalLocked', mutation: false }));

const createTotalLockedForPropertyCaller = (contract) => async (address) => execute({
    contract,
    method: 'totalLockedForProperty',
    args: [address],
    mutation: false,
});

const createGetLockedupPropertiesCaller = (contract) => always(execute({
    contract,
    method: 'getLockedupProperties',
    mutation: false,
}));

const createLockupContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...lockupAbi], provider);
    return {
        withdrawByPosition: createWithdrawByPositionCaller(contract),
        calculateWithdrawableInterestAmountByPosition: createcalculateWithdrawableInterestAmountByPositionCaller(contract),
        calculateCumulativeHoldersRewardAmount: createCalculateCumulativeHoldersRewardAmountCaller(contract),
        calculateCumulativeRewardPrices: createCalculateCumulativeRewardPricesCaller(contract),
        calculateRewardAmount: createCalculateRewardAmountCaller(contract),
        cap: createCapCaller(contract),
        depositToProperty: createDepositToPropertyCaller(contract),
        depositToPosition: createDepositToPositionCaller(contract),
        totalLocked: createTotalLockedCaller(contract),
        totalLockedForProperty: createTotalLockedForPropertyCaller(contract),
        getLockedupProperties: createGetLockedupPropertiesCaller(contract),
        contract: () => contract,
    };
};

const addressRegistryAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_key',
                type: 'string',
            },
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'setRegistry',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_key',
                type: 'string',
            },
        ],
        name: 'registries',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createRegistriesCaller = (contract) => (key) => execute({
    contract,
    method: 'registries',
    args: [key],
    mutation: false,
});

const createRegistryContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...addressRegistryAbi], provider);
    return {
        registries: createRegistriesCaller(contract),
        contract: () => contract,
    };
};

const cache$b = new WeakMap();
const clientsRegistry = async (provider) => {
    const res = cache$b.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const l1Info = l1AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId));
            const l1 = l1Info
                ? createRegistryContract$1(provider)(l1Info.registry)
                : undefined;
            const l2 = ((data) => data
                ? createRegistryContract(provider)(data.map.registry)
                : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$b.set(provider, results);
            return results;
        })());
    return res;
};

const cache$a = new WeakMap();
const clientsLockup = async (provider) => {
    const res = cache$a.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const [registry] = await clientsRegistry(provider);
            const l1 = registry
                ? createLockupContract$1(provider)(await registry.lockup())
                : undefined;
            const l2 = ((data) => data ? createLockupContract(provider)(data.map.lockup) : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$a.set(provider, results);
            return results;
        })());
    return res;
};

const createDevContract = createErc20Contract;

const cache$9 = new WeakMap();
const clientsDev = async (provider) => {
    const res = cache$9.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const [registry] = await clientsRegistry(provider);
            const l1 = registry
                ? createDevContract$1(provider)(await registry.token())
                : undefined;
            const l2 = ((data) => data ? createDevContract(provider)(data.map.token) : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$9.set(provider, results);
            return results;
        })());
    return res;
};

/* eslint-disable functional/functional-parameters */
const approveIfNeeded = async (factoryOptions) => {
    const client = factoryOptions.token
        ? createErc20Contract(factoryOptions.provider)(factoryOptions.token)
        : await clientsDev(factoryOptions.provider).then(([l1, l2]) => l1 ?? l2);
    const allowance = await whenDefinedAll([client, factoryOptions.to], ([x, to]) => x.allowance(factoryOptions.from, to));
    return whenDefinedAll([client, factoryOptions.to], ([dev, to]) => {
        return BigInt(allowance ?? 0) < BigInt(factoryOptions.requiredAmount)
            ? {
                approvalNeeded: true,
                approveIfNeeded: async (options) => {
                    const res = await dev.approve(to, options?.amount ?? factoryOptions.requiredAmount, options?.overrides);
                    return {
                        ...res,
                        waitNeeded: true,
                        waitOrSkipApproval: async () => {
                            const repeipt = await res.wait();
                            return {
                                ...repeipt,
                                run: () => factoryOptions.callback(repeipt),
                            };
                        },
                    };
                },
            }
            : {
                approvalNeeded: false,
                approveIfNeeded: async (options) => {
                    return {
                        waitNeeded: false,
                        waitOrSkipApproval: async () => ({
                            run: () => factoryOptions.callback(),
                        }),
                    };
                },
            };
    });
};

const positionsCreate = async (options) => {
    const [l1, l2] = await clientsLockup(options.provider);
    return l1 || l2
        ? ((res) => res)(await approveIfNeeded({
            provider: options.provider,
            requiredAmount: options.amount,
            from: options.from,
            to: await ((x) => x?.contract().getAddress())(l1 ?? l2),
            callback: (receipt) => l1
                ? l1.depositToProperty(options.destination, options.amount, options.payload, options.overrides)
                : l2
                    ? l2.depositToProperty(options.destination, options.amount, options.payload, options.overrides)
                    : undefined,
        }))
        : undefined;
};

const swapAbiV2 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_uniswapRouterAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_devAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_lockupAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sTokensAddress',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'payee',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'Deposited',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'payee',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'Withdrawn',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
        ],
        name: 'claim',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'devAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'user',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
        ],
        name: 'gatewayFees',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'gatewayOf',
        outputs: [
            {
                internalType: 'uint256',
                name: 'input',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'fee',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'ethAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedDevForEth',
        outputs: [
            {
                internalType: 'uint256[]',
                name: '',
                type: 'uint256[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'devAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedEthForDev',
        outputs: [
            {
                internalType: 'uint256[]',
                name: '',
                type: 'uint256[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'lockupAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'sTokensAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: 'payload',
                type: 'bytes32',
            },
        ],
        name: 'swapEthAndStakeDev',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: 'payload',
                type: 'bytes32',
            },
            {
                internalType: 'address payable',
                name: 'gatewayAddress',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'gatewayFee',
                type: 'uint256',
            },
        ],
        name: 'swapEthAndStakeDev',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'uniswapRouter',
        outputs: [
            {
                internalType: 'contract IUniswapV2Router02',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const swapAbiV3 = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_wethAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_devAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_lockupAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sTokensAddress',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        inputs: [],
        name: 'devAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'ethAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedDevForEth',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'devAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedEthForDev',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'lockupAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'quoter',
        outputs: [
            {
                internalType: 'contract IQuoter',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'sTokensAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'property',
                type: 'address',
            },
        ],
        name: 'swapEthAndStakeDev',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'uniswapRouter',
        outputs: [
            {
                internalType: 'contract ISwapRouter',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'wethAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createGetEstimatedDevForEthCaller = (contract) => async (ethAmount) => {
    const res = await execute({
        contract,
        method: 'getEstimatedDevForEth',
        args: [ethAmount],
        mutation: false,
        static: true,
    });
    return Array.isArray(res) ? res[1] : res;
};

const createGetEstimatedEthForDevCaller = (contract) => async (devAmount) => {
    const res = await execute({
        contract,
        method: 'getEstimatedEthForDev',
        args: [devAmount],
        mutation: false,
        static: true,
    });
    return Array.isArray(res) ? res[0] : res;
};

/* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
const createSwapEthAndStakeDevCaller = (contract) => async (propertyAddress, deadline, payload, overrides, gatewayAddress, gatewayBasisPoints) => {
    const args = gatewayAddress && gatewayBasisPoints
        ? [
            propertyAddress,
            String(deadline),
            payload ?? ZeroHash,
            gatewayAddress,
            gatewayBasisPoints,
        ]
        : [propertyAddress, String(deadline), payload ?? ZeroHash];
    return execute({
        contract,
        method: 'swapEthAndStakeDev',
        mutation: true,
        args,
        overrides,
        interface: gatewayAddress && gatewayBasisPoints
            ? 'address,uint256,bytes32,address,uint256'
            : 'address,uint256,bytes32',
    });
};

const createSwapEthAndStakeDevPolygonCaller = (contract) => async (propertyAddress, amount, deadline, payload, overrides, gatewayAddress, gatewayBasisPoints) => execute({
    contract,
    method: 'swapEthAndStakeDev',
    mutation: true,
    args: gatewayAddress && gatewayBasisPoints
        ? [
            propertyAddress,
            amount,
            String(deadline),
            payload,
            gatewayAddress,
            gatewayBasisPoints,
        ]
        : [propertyAddress, amount, String(deadline), payload],
    overrides,
    interface: gatewayAddress && gatewayBasisPoints
        ? 'address,uint256,uint256,bytes32,address,uint256'
        : 'address,uint256,uint256,bytes32',
});

const swapAbiV3Polygon = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_wethAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_devAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_lockupAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sTokensAddress',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'payee',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'Deposited',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'payee',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'Withdrawn',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
        ],
        name: 'claim',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'devAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'user',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
        ],
        name: 'gatewayFees',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'gatewayOf',
        outputs: [
            {
                internalType: 'uint256',
                name: 'input',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'fee',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'ethAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedDevForEth',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: 'devAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedEthForDev',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'lockupAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'quoter',
        outputs: [
            {
                internalType: 'contract IQuoter',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'sTokensAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: 'payload',
                type: 'bytes32',
            },
        ],
        name: 'swapEthAndStakeDev',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: 'payload',
                type: 'bytes32',
            },
            {
                internalType: 'address payable',
                name: 'gatewayAddress',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'gatewayFee',
                type: 'uint256',
            },
        ],
        name: 'swapEthAndStakeDev',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'uniswapRouter',
        outputs: [
            {
                internalType: 'contract ISwapRouter',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'wethAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createSwapContract = (provider, v = 'v3') => (address) => {
    const contract = new ethers.Contract(address, v === 'v3'
        ? [...swapAbiV3]
        : v === 'v3_polygon'
            ? [...swapAbiV3Polygon]
            : [...swapAbiV2], provider);
    return {
        getEstimatedDevForEth: createGetEstimatedDevForEthCaller(contract),
        getEstimatedEthForDev: createGetEstimatedEthForDevCaller(contract),
        swapEthAndStakeDevCaller: createSwapEthAndStakeDevCaller(contract),
        swapEthAndStakeDevPolygonCaller: createSwapEthAndStakeDevPolygonCaller(contract),
        contract: () => contract,
    };
};

const cache$8 = new WeakMap();
const polygonIDs = [137, 80001];
const clientsUtilsSwapForStake = async (provider) => {
    const res = cache$8.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const detectedNetwork = AgentAvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId));
            const l2 = detectedNetwork
                ? ((v) => createSwapContract(provider, v)(detectedNetwork.map.swap.v3 ?? detectedNetwork.map.swap.v2))(polygonIDs.some((id) => id === Number(net?.chainId))
                    ? 'v3_polygon'
                    : detectedNetwork.map.swap.v3 !== undefined
                        ? 'v3'
                        : 'v2')
                : undefined;
            const results = [undefined, l2, detectedNetwork?.map.weth];
            // eslint-disable-next-line functional/no-expression-statement
            cache$8.set(provider, results);
            return results;
        })());
    return res;
};

const positionsCreateWithEth = async (options) => {
    const [, l2] = await clientsUtilsSwapForStake(options.provider);
    return l2
        ? {
            estimatedDev: options.ethAmount
                ? await l2.getEstimatedDevForEth(options.ethAmount)
                : 'No ethAmount provided',
            estimatedEth: options.devAmount
                ? await l2.getEstimatedEthForDev(options.devAmount)
                : 'No devAmount provided',
            create: async () => {
                const ethAmount = options.ethAmount
                    ? options.ethAmount
                    : options.devAmount
                        ? await l2.getEstimatedEthForDev(options.devAmount)
                        : 'Neither ethAmount nor devAmount provided';
                const _overrides = {
                    overrides: {
                        ...{ value: ethAmount },
                        ...options.overrides?.overrides,
                    },
                };
                const deadline = options.deadline ??
                    ((await options.provider.provider?.getBlock('latest'))?.timestamp ??
                        Math.floor(new Date().getTime() / 1000)) + 300;
                return options.gatewayAddress &&
                    typeof options.gatewayBasisPoints === 'number'
                    ? await l2.swapEthAndStakeDevCaller(options.destination, deadline, options.payload ?? ZeroHash, _overrides, options.gatewayAddress, String(options.gatewayBasisPoints))
                    : await l2.swapEthAndStakeDevCaller(options.destination, deadline, options.payload ?? ZeroHash, _overrides);
            },
        }
        : undefined;
};

const positionsCreateWithEthForPolygon = async (options) => {
    const [, l2, weth] = await clientsUtilsSwapForStake(options.provider);
    return l2 && weth
        ? {
            estimatedDev: options.ethAmount
                ? await l2.getEstimatedDevForEth(options.ethAmount)
                : 'No ethAmount provided',
            estimatedEth: options.devAmount
                ? await l2.getEstimatedEthForDev(options.devAmount)
                : 'No devAmount provided',
            create: async () => whenDefined(options.from, async (from) => {
                const ethAmount = options.ethAmount
                    ? options.ethAmount
                    : options.devAmount
                        ? await l2.getEstimatedEthForDev(options.devAmount)
                        : 'Neither ethAmount nor devAmount provided';
                const _overrides = {
                    overrides: {
                        ...options.overrides?.overrides,
                    },
                };
                return approveIfNeeded({
                    provider: options.provider,
                    requiredAmount: ethAmount,
                    from,
                    token: weth,
                    to: await l2.contract().getAddress(),
                    callback: async () => {
                        const deadline = options.deadline
                            ? options.deadline
                            : ((await options.provider.provider?.getBlock('latest'))
                                ?.timestamp ??
                                Math.floor(new Date().getTime() / 1000)) + 300;
                        return options.gatewayAddress &&
                            typeof options.gatewayBasisPoints === 'number'
                            ? l2.swapEthAndStakeDevPolygonCaller(options.destination, ethAmount, deadline, options.payload ?? ZeroHash, _overrides, options.gatewayAddress, String(options.gatewayBasisPoints))
                            : l2.swapEthAndStakeDevPolygonCaller(options.destination, ethAmount, deadline, options.payload ?? ZeroHash, _overrides);
                    },
                });
            }),
        }
        : undefined;
};

const swapArbitraryTokensAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'payee',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'Deposited',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'uint8',
                name: 'version',
                type: 'uint8',
            },
        ],
        name: 'Initialized',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'payee',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'Withdrawn',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
        ],
        name: 'claim',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'devAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'user',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
        ],
        name: 'gatewayFees',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'gatewayOf',
        outputs: [
            {
                internalType: 'address',
                name: 'token',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'input',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: 'fee',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes',
                name: 'path',
                type: 'bytes',
            },
            {
                internalType: 'uint256',
                name: 'tokenAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedDevForTokens',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'bytes',
                name: 'path',
                type: 'bytes',
            },
            {
                internalType: 'uint256',
                name: 'devAmount',
                type: 'uint256',
            },
        ],
        name: 'getEstimatedTokensForDev',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_devAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_lockupAddress',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_sTokensAddress',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'lockupAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'quoter',
        outputs: [
            {
                internalType: 'contract IQuoter',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'sTokensAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'bytes',
                name: '_path',
                type: 'bytes',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amountOut',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
            {
                internalType: 'address payable',
                name: '_gatewayAddress',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_gatewayFee',
                type: 'uint256',
            },
        ],
        name: 'swapTokensAndStakeDev',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'contract IERC20',
                name: '_token',
                type: 'address',
            },
            {
                internalType: 'bytes',
                name: '_path',
                type: 'bytes',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amountOut',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
            {
                internalType: 'address payable',
                name: '_gatewayAddress',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_gatewayFee',
                type: 'uint256',
            },
        ],
        name: 'swapTokensAndStakeDev',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'bytes',
                name: '_path',
                type: 'bytes',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amountOut',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
        ],
        name: 'swapTokensAndStakeDev',
        outputs: [],
        stateMutability: 'payable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'contract IERC20',
                name: '_token',
                type: 'address',
            },
            {
                internalType: 'bytes',
                name: '_path',
                type: 'bytes',
            },
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_amountOut',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_deadline',
                type: 'uint256',
            },
            {
                internalType: 'bytes32',
                name: '_payload',
                type: 'bytes32',
            },
        ],
        name: 'swapTokensAndStakeDev',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'uniswapRouter',
        outputs: [
            {
                internalType: 'contract ISwapRouter',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const pathOf = (_path) => {
    const abi = _path.map((value) => typeof value === 'string'
        ? 'address'
        : typeof value === 'bigint'
            ? 'uint24'
            : undefined);
    const path = solidityPacked(abi, _path);
    return path;
};

const createGetEstimatedDevForTokensCaller = (contract) => async (path, tokenAmount) => {
    const res = await execute({
        contract,
        method: 'getEstimatedDevForTokens',
        args: [pathOf(path), tokenAmount],
        mutation: false,
        static: true,
    });
    return res;
};

const createGetEstimatedTokensForDevCaller = (contract) => async (path, devAmount) => {
    const res = await execute({
        contract,
        method: 'getEstimatedTokensForDev',
        args: [pathOf(path), devAmount],
        mutation: false,
        static: true,
    });
    return res;
};

/* eslint-disable @typescript-eslint/prefer-readonly-parameter-types */
const createSwapTokensAndStakeDevCaller = (contract) => async (to, path, propertyAddress, amountOut, deadline, amount, payload, token, gatewayAddress, gatewayBasisPoints, overrides) => {
    const args = token && token !== ZeroAddress
        ? // Overload: ERC20
            gatewayAddress && gatewayBasisPoints
                ? [
                    to,
                    token,
                    pathOf(path),
                    propertyAddress,
                    amount,
                    amountOut,
                    String(deadline),
                    payload ?? ZeroHash,
                    gatewayAddress,
                    gatewayBasisPoints,
                ]
                : [
                    to,
                    token,
                    pathOf(path),
                    propertyAddress,
                    amount,
                    amountOut,
                    String(deadline),
                    payload ?? ZeroHash,
                ]
        : gatewayAddress && gatewayBasisPoints
            ? // Overload: Payable
                [
                    to,
                    pathOf(path),
                    propertyAddress,
                    amountOut,
                    String(deadline),
                    payload ?? ZeroHash,
                    gatewayAddress,
                    gatewayBasisPoints,
                ]
            : [
                to,
                pathOf(path),
                propertyAddress,
                amountOut,
                String(deadline),
                payload ?? ZeroHash,
            ];
    return execute({
        contract,
        method: 'swapTokensAndStakeDev',
        mutation: true,
        args,
        overrides,
        interface: token && token !== ZeroAddress
            ? // Overload: ERC20
                gatewayAddress && gatewayBasisPoints
                    ? 'address,address,bytes,address,uint256,uint256,uint256,bytes32,address,uint256'
                    : 'address,address,bytes,address,uint256,uint256,uint256,bytes32'
            : gatewayAddress && gatewayBasisPoints
                ? // Overload: Payable
                    'address,bytes,address,uint256,uint256,bytes32,address,uint256'
                : 'address,bytes,address,uint256,uint256,bytes32',
    });
};

const createSwapArbitraryTokensContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...swapArbitraryTokensAbi], provider);
    return {
        getEstimatedDevForTokens: createGetEstimatedDevForTokensCaller(contract),
        getEstimatedTokensForDev: createGetEstimatedTokensForDevCaller(contract),
        swapTokensAndStakeDev: createSwapTokensAndStakeDevCaller(contract),
        contract: () => contract,
    };
};

const cache$7 = new WeakMap();
const clientsUtilsSwapTokensForStake = async (provider) => {
    const res = cache$7.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const detectedNetwork = AgentAvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId));
            const cont = detectedNetwork
                ? createSwapArbitraryTokensContract(provider)(detectedNetwork.map.swapArbitraryTokens?.swap || '')
                : undefined;
            const results = [undefined, cont];
            // eslint-disable-next-line functional/no-expression-statement
            cache$7.set(provider, results);
            return results;
        })());
    return res;
};

const hasTokens = (options) => options.token !== undefined && options.token !== ZeroAddress;
async function positionsCreateWithAnyTokens(options) {
    const [, cont] = await clientsUtilsSwapTokensForStake(options.provider);
    const useERC20 = hasTokens(options);
    return cont
        ? {
            estimatedDev: options.tokenAmount
                ? await cont.getEstimatedDevForTokens(options.path, options.tokenAmount)
                : 'No tokenAmount provided',
            estimatedTokens: options.devAmount
                ? await cont.getEstimatedTokensForDev(options.path, options.devAmount)
                : 'No devAmount provided',
            create: async () => {
                const tokenAmount = options.tokenAmount
                    ? options.tokenAmount
                    : options.devAmount
                        ? await cont.getEstimatedTokensForDev(options.path, options.devAmount)
                        : 'Neither tokenAmount nor devAmount provided';
                const _overrides = {
                    overrides: {
                        ...{ value: useERC20 ? undefined : tokenAmount },
                        ...options.overrides?.overrides,
                    },
                };
                const devAmountOut = options.devAmountOut ? options.devAmountOut : '0';
                const deadline = options.deadline
                    ? options.deadline
                    : ((await options.provider.provider?.getBlock('latest'))
                        ?.timestamp ?? Math.floor(new Date().getTime() / 1000)) + 300;
                return useERC20
                    ? whenDefined(options.from, async (from) => {
                        return approveIfNeeded({
                            provider: options.provider,
                            requiredAmount: tokenAmount,
                            from,
                            token: options.token,
                            to: await cont.contract().getAddress(),
                            callback: async () => {
                                return options.gatewayAddress &&
                                    typeof options.gatewayBasisPoints === 'number'
                                    ? cont.swapTokensAndStakeDev(options.mintTo, options.path, options.destination, devAmountOut, deadline, tokenAmount, options.payload, options.token, options.gatewayAddress, options.gatewayBasisPoints.toString(), _overrides)
                                    : cont.swapTokensAndStakeDev(options.mintTo, options.path, options.destination, devAmountOut, deadline, tokenAmount, options.payload, options.token, undefined, undefined, _overrides);
                            },
                        });
                    })
                    : {
                        approvalNeeded: false,
                        approveIfNeeded: async (
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        _unused_) => ({
                            waitNeeded: false,
                            waitOrSkipApproval: async () => ({
                                run: () => options.gatewayAddress &&
                                    typeof options.gatewayBasisPoints === 'number'
                                    ? cont.swapTokensAndStakeDev(options.mintTo, options.path, options.destination, devAmountOut, deadline, tokenAmount, options.payload, undefined, options.gatewayAddress, options.gatewayBasisPoints.toString(), _overrides)
                                    : cont.swapTokensAndStakeDev(options.mintTo, options.path, options.destination, devAmountOut, deadline, tokenAmount, options.payload, undefined, undefined, undefined, _overrides),
                            }),
                        }),
                    };
            },
        }
        : undefined;
}

const metricsFactoryAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'Destroy',
        type: 'event',
    },
    {
        inputs: [],
        name: 'authenticatedPropertiesCount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'isMetrics',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'metricsCount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_metrics',
                type: 'address',
            },
        ],
        name: 'destroy',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'metricsCountPerProperty',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'metricsOfProperty',
        outputs: [
            {
                internalType: 'address[]',
                name: '',
                type: 'address[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'hasAssets',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createAuthenticatedPropertiesCountCaller = (contract) => always(execute({
    contract,
    method: 'authenticatedPropertiesCount',
    mutation: false,
}));

const createMetricsOfPropertyCaller = (contract) => (propertyAddress) => execute({
    contract,
    method: 'metricsOfProperty',
    args: [propertyAddress],
    mutation: false,
});

const createMetricsCountCaller = (contract) => always(execute({
    contract,
    method: 'metricsCount',
    mutation: false,
}).then(Number));

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const createMetricsFactoryContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...metricsFactoryAbi], provider);
    return {
        authenticatedPropertiesCount: createAuthenticatedPropertiesCountCaller(contract),
        metricsCount: createMetricsCountCaller(contract),
        metricsOfProperty: createMetricsOfPropertyCaller(contract),
        contract: () => contract,
    };
};

const cache$6 = new WeakMap();
const clientsMetricsFactory = async (provider) => {
    const res = cache$6.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const l1 = undefined;
            const l2 = ((data) => data
                ? createMetricsFactoryContract(provider)(data.map.metricsFactory)
                : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$6.set(provider, results);
            return results;
        })());
    return res;
};

const cache$5 = new WeakMap();
const clientsMetricsGroup = async (provider) => {
    const res = cache$5.get(provider) ??
        (await (async () => {
            const [registry] = await clientsRegistry(provider);
            const l1 = registry
                ? createMetricsGroupContract(provider)(await registry.metricsGroup())
                : undefined;
            const l2 = undefined;
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$5.set(provider, results);
            return results;
        })());
    return res;
};

const policyAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        inputs: [],
        name: 'marketVotingSeconds',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'policyVotingSeconds',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_lockups',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_assets',
                type: 'uint256',
            },
        ],
        name: 'rewards',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_reward',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_lockups',
                type: 'uint256',
            },
        ],
        name: 'holdersShare',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_assets',
                type: 'uint256',
            },
            {
                internalType: 'uint256',
                name: '_propertyAssets',
                type: 'uint256',
            },
        ],
        name: 'authenticationFee',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '_supply',
                type: 'uint256',
            },
        ],
        name: 'shareOfTreasury',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'pure',
        type: 'function',
    },
];

const createMarketVotingSecondsCaller = (contract) => always(execute({
    contract,
    method: 'marketVotingSeconds',
    mutation: false,
}));

const createPolicyVotingSecondsCaller = (contract) => always(execute({
    contract,
    method: 'policyVotingSeconds',
    mutation: false,
}));

const createPolicyContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...policyAbi], provider);
    return {
        holdersShare: createHoldersShareCaller(contract),
        rewards: createRewardsCaller$1(contract),
        authenticationFee: createAuthenticationFeeCaller(contract),
        marketVotingSeconds: createMarketVotingSecondsCaller(contract),
        policyVotingSeconds: createPolicyVotingSecondsCaller(contract),
        shareOfTreasury: createShareOfTreasuryCaller(contract),
        contract: () => contract,
    };
};

// eslint-disable-next-line functional/prefer-readonly-type
const cache$4 = new WeakMap();
const clientsPolicy = async (provider) => {
    const res = cache$4.get(provider) ??
        (await (async () => {
            const [registryl1, registryl2] = await clientsRegistry(provider);
            const l1 = registryl1
                ? createPolicyContract$1(provider)(await registryl1.policy())
                : undefined;
            const l2 = registryl2
                ? createPolicyContract(provider)(await registryl2.registries('Policy'))
                : undefined;
            const results = [l1, l2];
            cache$4.get(provider);
            // eslint-disable-next-line functional/no-expression-statement
            cache$4.set(provider, results);
            return results;
        })());
    return res;
};

const estimationsAPY = async (options) => {
    const [[l1P, l2P], [l1L, l2L], [, l2MF], [l1MG]] = await Promise.all([
        clientsPolicy(options.provider),
        clientsLockup(options.provider),
        clientsMetricsFactory(options.provider),
        clientsMetricsGroup(options.provider),
    ]);
    const tvl = l1L
        ? await l1L.getAllValue()
        : l2L
            ? await l2L.totalLocked()
            : undefined;
    const assets = l1MG
        ? await l1MG.totalIssuedMetrics()
        : l2MF
            ? await l2MF.metricsCount()
            : undefined;
    const yeild = await whenDefinedAll([tvl, assets, l1P || l2P], ([_tvl, _assets, policy]) => policy.rewards(_tvl, _assets.toString()));
    const holders = await whenDefinedAll([tvl, yeild, l1P || l2P], ([_tvl, y, policy]) => policy.holdersShare(y, _tvl));
    const annualYeild = whenDefined(yeild, (y) => BigInt(y) * (l1P ? BigInt(2102400) : BigInt(31536000)));
    const shareOfHolders = whenDefinedAll([holders, yeild], ([hol, y]) => new BigNumber(hol).div(y).toNumber());
    const shareOfStakers = whenDefined(shareOfHolders, (hs) => 1 - hs);
    const apyForStakers = whenDefinedAll([annualYeild, shareOfStakers, tvl], ([ay, sh, tv]) => new BigNumber(ay.toString()).times(sh).div(tv).toNumber());
    const apyForHolders = whenDefinedAll([annualYeild, shareOfHolders, tvl], ([ay, sh, tv]) => new BigNumber(ay.toString()).times(sh).div(tv).toNumber());
    return [apyForStakers, apyForHolders];
};

const propertiesAssets = async (options) => {
    const marketSet = unnest(flatten(values(marketAddresses).map(values)).map(toPairs));
    const [, metricsFactory] = await clientsMetricsFactory(options.provider);
    const metricsContract = createMetricsContract(options.provider);
    const marketContract = createMarketContract(options.provider);
    const marketBehaviorContract = createMarketBehaviorContract(options.provider);
    const listOfMetrics = await whenDefined(metricsFactory, (c) => c.metricsOfProperty(options.destination));
    const listOfMetricsContract = await whenDefined(listOfMetrics, (x) => Promise.all(x.map(metricsContract)));
    const listOfMarketContract = await whenDefined(listOfMetricsContract, (m) => Promise.all(m.map(async (metrics) => ({
        metrics,
        market: marketContract(await metrics.market()),
    }))));
    const marketBehaviors = await whenDefined(listOfMarketContract, (m) => Promise.all(m.map(async (cont) => ({
        marketBehavior: marketBehaviorContract(await cont.market.behavior()),
        ...cont,
    }))));
    const results = await whenDefined(marketBehaviors, (mb) => Promise.all(mb.map(async (cont) => {
        const [metrics, market] = await Promise.all([
            cont.metrics.contract().getAddress(),
            cont.market.contract().getAddress(),
        ]);
        const marketSlug = marketSet.find(([, addr]) => addr === market)?.[0];
        return {
            id: await cont.marketBehavior.getId(metrics),
            market,
            marketSlug,
        };
    })));
    return results;
};

const marketFactoryAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'previousOwner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'OwnershipTransferred',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        name: 'enabledMarkets',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'isMarket',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'isPotentialMarket',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'marketsCount',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'owner',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'renounceOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'newOwner',
                type: 'address',
            },
        ],
        name: 'transferOwnership',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_addr',
                type: 'address',
            },
        ],
        name: 'enable',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'getEnabledMarkets',
        outputs: [
            {
                internalType: 'address[]',
                name: '',
                type: 'address[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createGetEnabledMarketsCaller = (contract) => always(execute({
    contract,
    method: 'getEnabledMarkets',
    mutation: false,
}));

const createMarketFactoryContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...marketFactoryAbi], provider);
    return {
        create: createCreateCaller(contract),
        getEnabledMarkets: createGetEnabledMarketsCaller(contract),
        contract: () => contract,
    };
};

const cache$3 = new WeakMap();
const clientsMarketFactory = async (provider) => {
    const res = cache$3.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const [registry] = await clientsRegistry(provider);
            const l1 = registry
                ? createMarketFactoryContract$1(provider)(await registry.marketFactory())
                : undefined;
            const l2 = ((data) => data
                ? createMarketFactoryContract(provider)(data.map.marketFactory)
                : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$3.set(provider, results);
            return results;
        })());
    return res;
};

const propertyAbi = [
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_own',
                type: 'address',
            },
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'constructor',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Approval',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'string',
                name: '_old',
                type: 'string',
            },
            {
                indexed: false,
                internalType: 'string',
                name: '_new',
                type: 'string',
            },
        ],
        name: 'ChangeName',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: false,
                internalType: 'string',
                name: '_old',
                type: 'string',
            },
            {
                indexed: false,
                internalType: 'string',
                name: '_new',
                type: 'string',
            },
        ],
        name: 'ChangeSymbol',
        type: 'event',
    },
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'from',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'to',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'value',
                type: 'uint256',
            },
        ],
        name: 'Transfer',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'owner',
                type: 'address',
            },
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
        ],
        name: 'allowance',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'approve',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'account',
                type: 'address',
            },
        ],
        name: 'balanceOf',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'subtractedValue',
                type: 'uint256',
            },
        ],
        name: 'decreaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: 'spender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: 'addedValue',
                type: 'uint256',
            },
        ],
        name: 'increaseAllowance',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'totalSupply',
        outputs: [
            {
                internalType: 'uint256',
                name: '',
                type: 'uint256',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'author',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'name',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'symbol',
        outputs: [
            {
                internalType: 'string',
                name: '',
                type: 'string',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'decimals',
        outputs: [
            {
                internalType: 'uint8',
                name: '',
                type: 'uint8',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
        ],
        name: 'changeName',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
        ],
        name: 'changeSymbol',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'transfer',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                internalType: 'address',
                name: '_to',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'transferFrom',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_sender',
                type: 'address',
            },
            {
                internalType: 'uint256',
                name: '_value',
                type: 'uint256',
            },
        ],
        name: 'withdraw',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [],
        name: 'getBalances',
        outputs: [
            {
                components: [
                    {
                        internalType: 'address',
                        name: 'account',
                        type: 'address',
                    },
                    {
                        internalType: 'uint256',
                        name: 'balance',
                        type: 'uint256',
                    },
                ],
                internalType: 'struct IProperty.PropertyBalance[]',
                name: '',
                type: 'tuple[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
];

const createGetBalancesCaller = (contract) => always(execute({
    contract,
    method: 'getBalances',
    mutation: false,
}));

const createPropertyContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...propertyAbi], provider);
    return {
        totalSupply: createTotalSupplyCaller(contract),
        balanceOf: createBalanceOfCaller(contract),
        transfer: createTransferCaller(contract),
        allowance: createAllowanceCaller(contract),
        approve: createApproveCaller(contract),
        transferFrom: createTransferFromCaller(contract),
        name: createNameCaller(contract),
        symbol: createSymbolCaller(contract),
        decimals: createDecimalsCaller(contract),
        author: createAuthorCaller(contract),
        changeName: createChangeNameCaller(contract),
        changeSymbol: createChangeSymbolCaller(contract),
        getBalances: createGetBalancesCaller(contract),
        contract: () => contract,
    };
};

// eslint-disable-next-line functional/prefer-readonly-type
const cache$2 = new WeakMap();
const clientsProperty = async (provider, tokenAddress) => {
    const res = cache$2.get(provider)?.get(tokenAddress) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const [registry] = await clientsRegistry(provider);
            const l1 = registry
                ? createPropertyContract$1(provider)(tokenAddress)
                : undefined;
            const l2 = ((data) => data ? createPropertyContract(provider)(tokenAddress) : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            const map = cache$2.get(provider);
            // eslint-disable-next-line functional/no-expression-statement
            cache$2.set(provider, map
                ? map.set(tokenAddress, results)
                : new Map([[tokenAddress, results]]));
            return results;
        })());
    return res;
};

const propertyFactoryAbi = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_from',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'Create',
        type: 'event',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        name: 'isProperty',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'registryAddress',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_registry',
                type: 'address',
            },
        ],
        name: 'initialize',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
            {
                internalType: 'address',
                name: '_author',
                type: 'address',
            },
        ],
        name: 'create',
        outputs: [
            {
                internalType: 'address',
                name: '',
                type: 'address',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'string',
                name: '_name',
                type: 'string',
            },
            {
                internalType: 'string',
                name: '_symbol',
                type: 'string',
            },
            {
                internalType: 'address',
                name: '_market',
                type: 'address',
            },
            {
                internalType: 'string[]',
                name: '_args',
                type: 'string[]',
            },
        ],
        name: 'createAndAuthenticate',
        outputs: [
            {
                internalType: 'bool',
                name: '',
                type: 'bool',
            },
        ],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_author',
                type: 'address',
            },
        ],
        name: 'getPropertiesOfAuthor',
        outputs: [
            {
                internalType: 'address[]',
                name: '',
                type: 'address[]',
            },
        ],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            {
                internalType: 'address',
                name: '_property',
                type: 'address',
            },
        ],
        name: 'setPropertyAddress',
        outputs: [],
        stateMutability: 'nonpayable',
        type: 'function',
    },
];

const createCreateAndAuthenticateCaller = (contract, provider) => async (name, symbol, marketAddress, args, { metricsFactoryAddress }, overrides) => {
    const transaction = await execute({
        contract,
        method: 'createAndAuthenticate',
        args: [name, symbol, marketAddress, args],
        mutation: true,
        overrides,
    });
    const metricsFactoryContract = new ethers.Contract(metricsFactoryAddress, metricsFactoryAbi, provider);
    const createWaitForAuthentication = (propertyAddress) => () => new Promise((resolve) => {
        const subscriberdContract = metricsFactoryContract.on('Create', async (_, receiptPropertyAddress, metricsAddress) => {
            if (propertyAddress.toLowerCase() ===
                receiptPropertyAddress.toLocaleLowerCase()) {
                (await subscriberdContract).removeAllListeners();
                resolve(metricsAddress);
            }
        });
    });
    return new Promise((resolve) => {
        const subscribedContract = contract.on('Create', async (_, propertyAddress) => {
            (await subscribedContract).removeAllListeners();
            resolve({
                property: propertyAddress,
                transaction,
                waitForAuthentication: createWaitForAuthentication(propertyAddress),
            });
        });
    });
};

const createGetPropertiesOfAuthorCaller = (contract) => async (authorAddress) => {
    const res = await execute({
        contract,
        method: 'getPropertiesOfAuthor',
        args: [authorAddress],
        mutation: false,
    });
    return res;
};

const createPropertyFactoryContract = (provider) => (address) => {
    const contract = new ethers.Contract(address, [...propertyFactoryAbi], provider);
    return {
        create: createCreatePropertyCaller(contract),
        createAndAuthenticate: createCreateAndAuthenticateCaller(contract, provider),
        getPropertiesOfAuthor: createGetPropertiesOfAuthorCaller(contract),
        contract: () => contract,
    };
};

const cache$1 = new WeakMap();
const clientsPropertyFactory = async (provider) => {
    const res = cache$1.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const [registry] = await clientsRegistry(provider);
            const l1 = registry
                ? createPropertyFactoryContract$1(provider)(await registry.propertyFactory())
                : undefined;
            const l2 = ((data) => data
                ? createPropertyFactoryContract(provider)(data.map.propertyFactory)
                : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache$1.set(provider, results);
            return results;
        })());
    return res;
};

const cache = new WeakMap();
const clientsSTokens = async (provider) => {
    const res = cache.get(provider) ??
        (await (async () => {
            const net = await provider.provider?.getNetwork();
            const l1 = ((data) => data
                ? createSTokensContract(provider)(data.chainId === 1
                    ? addresses.eth.main.sTokens
                    : addresses.eth.ropsten.sTokens)
                : undefined)(l1AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const l2 = ((data) => data ? createSTokensContract(provider)(data.map.sTokens) : undefined)(l2AvailableNetworks.find(({ chainId }) => chainId === Number(net?.chainId)));
            const results = [l1, l2];
            // eslint-disable-next-line functional/no-expression-statement
            cache.set(provider, results);
            return results;
        })());
    return res;
};

export { addressConfigAbi, addresses, agentAddresses, allocatorAbi, approveIfNeeded, arrayify, client, clientsDev, clientsLockup, clientsMarketFactory, clientsMetricsFactory, clientsPolicy, clientsProperty, clientsPropertyFactory, clientsRegistry, clientsSTokens, clientsUtilsSwapForStake, clientsUtilsSwapTokensForStake as clientsUtilsSwapUsdcForStake, contractFactory, createAllocatorContract, createDetectSTokens, createDevContract$1 as createDevContract, createDevkitContract, createErc20Contract, createLockupContract$1 as createLockupContract, createMarketBehaviorContract, createMarketContract, createMarketFactoryContract$1 as createMarketFactoryContract, createMetricsContract, createMetricsGroupContract, createPolicyContract$1 as createPolicyContract, createPolicyFactoryContract, createPolicyGroupContract, createPropertyContract$1 as createPropertyContract, createPropertyFactoryContract$1 as createPropertyFactoryContract, createRegistryContract$1 as createRegistryContract, createSTokensContract, createSimpleCollectionsContract, createWithdrawContract, devAbi, erc20Abi, estimationsAPY, execute, lockupAbi$1 as lockupAbi, marketAbi, marketAddresses, marketBehaviorAbi, marketFactoryAbi$1 as marketFactoryAbi, metricsAbi, metricsFactoryAbi$1 as metricsFactoryAbi, metricsGroupAbi, policyAbi$1 as policyAbi, policyFactoryAbi, policyGroupAbi, positionsCreate, positionsCreateWithAnyTokens, positionsCreateWithEth, positionsCreateWithEthForPolygon, propertiesAssets, propertyAbi$1 as propertyAbi, propertyFactoryAbi$1 as propertyFactoryAbi, sTokensAbi, simpleCollectionsAbi, utils, withdrawAbi };
